import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'
import DesktopProgressBar from './DesktopProgressBar'
import MobileProgressSteps from './MobileProgressSteps'
import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'

/**
 * Progress indicator for the step-by-step advice flow.
 *
 * Chooses its presentation responsively based on the viewport width:
 *  - desktop: a single bar that fills proportionally as the user advances
 *  - mobile:  one tappable dash per step
 *
 * Width defaults to 100% on both desktop and mobile; pass `width` to override.
 * On desktop a 40px gutter is padded on each side so the bar doesn't touch the
 * screen edges.
 */
const AdviceProgressIndicator = props => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const { width, ...rest } = props

  // Mobile: 16px side padding matches AdviceTopNav so the progress indicator
  // lines up with the status pill.
  const containerStyle = isMobile
    ? { width: '100%', boxSizing: 'border-box', padding: '0 16px', margin: '12px 0 6px' }
    : { width: width || '100%', boxSizing: 'border-box', padding: '0 40px', margin: '20px 0 0' }

  return (
    <div style={containerStyle}>
      {isMobile ? <MobileProgressSteps {...rest} /> : <DesktopProgressBar {...rest} />}
    </div>
  )
}

AdviceProgressIndicator.propTypes = {
  /** Total number of steps in the flow, including the summary screen. */
  totalSteps: PropTypes.number.isRequired,
  /** The 1-based index of the step the user is currently on. */
  currentStep: PropTypes.number.isRequired,
  /** Called with the tapped step number (mobile dashes only). */
  onStepChange: PropTypes.func,
  /** Optional per-step labels, used for accessible dash labels on mobile. */
  stepLabels: PropTypes.arrayOf(PropTypes.string),
  /** Override the fill color (defaults to the client theme's primaryColor). */
  fillColor: PropTypes.string,
  /** Override the track color (defaults to the client theme's homeActionButtonBgColor). */
  trackColor: PropTypes.string,
  className: PropTypes.string,
  /** CSS width of the indicator (defaults to '100%'). */
  width: PropTypes.string,
}

export default AdviceProgressIndicator
